/**
 * The asset catalog (issue #205): the finished art staff published to it,
 * which freelancers browse and download to match the house style.
 */

/** The catalog's page size. */
export const CATALOG_PAGE_SIZE = 48;

/**
 * The asset types the catalog can show, in the Type filter's order. Tile
 * brushes aren't here: they have no single image under d3 to show yet.
 */
export const CATALOG_TYPES = [
    { value: 'image', label: 'Image' },
    { value: 'miniature', label: 'Miniature' },
    { value: 'tactical-map', label: 'Tactical Map' },
    { value: 'capsule', label: 'Capsule' },
    { value: 'hook', label: 'Hook' },
    { value: 'title-screen', label: 'Title Screen' },
];

/**
 * @typedef {object} CatalogFilter
 * @property {string} [query] part of the slug
 * @property {string} [type] an asset type
 * @property {string} [tag] a tag
 * @property {number} [page] the page, from 0
 */

/**
 * The query string that asks the API for a page of the catalog. Empty filters
 * are left out.
 * @param {CatalogFilter} filter
 * @returns {string}
 */
export function catalogQuery({ query = '', type = '', tag = '', page = 0 }) {
    const params = new URLSearchParams();
    if (query.trim()) params.set('query', query.trim());
    if (type) params.set('type', type);
    if (tag.trim()) params.set('tag', tag.trim().toLowerCase());
    params.set('page', String(page));
    params.set('pageSize', String(CATALOG_PAGE_SIZE));
    return params.toString();
}

/**
 * The name the catalog shows for an asset type.
 * @param {string} type
 * @returns {string}
 */
export function catalogTypeLabel(type) {
    return CATALOG_TYPES.find((t) => t.value === type)?.label ?? type;
}

/**
 * An image's dimensions, e.g. "400 × 300 px", or '' when they're unknown.
 * @param {number | null | undefined} width
 * @param {number | null | undefined} height
 * @returns {string}
 */
export function formatDimensions(width, height) {
    if (!width || !height) return '';
    return `${width} × ${height} px`;
}

/**
 * A file size for people, e.g. "1.2 MB".
 * @param {number | null | undefined} bytes
 * @returns {string}
 */
export function formatFileSize(bytes) {
    if (bytes == null) return '';
    if (bytes < 1024) return `${bytes} B`;
    const units = ['KB', 'MB', 'GB'];
    let size = bytes / 1024;
    let unit = 0;
    while (size >= 1024 && unit < units.length - 1) {
        size /= 1024;
        unit++;
    }
    return `${size.toFixed(1)} ${units[unit]}`;
}

/**
 * The range of the catalog a page shows, e.g. "49–96 of 120", or '' for an
 * empty catalog.
 * @param {number} page the page, from 0
 * @param {number} count how many assets the page holds
 * @param {number} total how many assets match
 * @returns {string}
 */
export function pageRange(page, count, total) {
    if (total === 0 || count === 0) return '';
    const first = page * CATALOG_PAGE_SIZE + 1;
    return `${first}–${first + count - 1} of ${total}`;
}

/**
 * @typedef {object} CatalogVersion
 * @property {number} version
 * @property {boolean} is_current
 * @property {string} image_url
 * @property {string} file_name
 * @property {number} file_size
 * @property {string} mime_type
 * @property {number | null} width
 * @property {number | null} height
 * @property {import('./authors.js').Author | null} [author] who made it, when known
 */

/**
 * The version of a catalog asset to show: the one asked for, or else the
 * catalog file's (the current image), or else the newest.
 * @param {{ version: number, versions?: CatalogVersion[] }} asset
 * @param {number | null} [version]
 * @returns {CatalogVersion | undefined}
 */
export function pickVersion(asset, version) {
    const versions = asset.versions ?? [];
    return (
        versions.find((v) => v.version === version) ??
        versions.find((v) => v.version === asset.version) ??
        versions[0]
    );
}

/**
 * A version's caption, e.g. "v3 · current".
 * @param {CatalogVersion} v
 * @returns {string}
 */
export function versionCaption(v) {
    return v.is_current ? `v${v.version} · current` : `v${v.version}`;
}
